(function () {
  'use strict';

  if (window.metaboxMwResolver) return;

  const normalizeHelpers = window.metaboxMwNormalize || {};
  const normalizeOptionName = normalizeHelpers.normalizeOptionName || ((value) => String(value || '').trim().toLowerCase());
  const normalizeEntityName = normalizeHelpers.normalizeEntityName || normalizeOptionName;

  function getActiveComponentContext() {
    const envelope = window.__lastMetaboxEnvelope;
    const configurationTree = Array.isArray(envelope && envelope.configurationTree) ? envelope.configurationTree : [];
    const activeIds = new Set(configurationTree.map((node) => node && node.id).filter(Boolean));
    const rootNode = configurationTree.find((node) => node && !node.parentId);

    return {
      activeIds,
      rootId: rootNode ? rootNode.id : null,
    };
  }

  function getConfiguratorComponents() {
    const configurator = window.__lastMetaboxEnvelope && window.__lastMetaboxEnvelope.configurator;
    const components = configurator && configurator.components;
    return Array.isArray(components) ? components : [];
  }

  function getRootComponents() {
    return getConfiguratorComponents().filter((component) => Boolean(component?.componentType?.isRoot));
  }

  function getActiveRootComponent() {
    const activeContext = getActiveComponentContext();
    if (!activeContext.rootId) return null;
    return getConfiguratorComponents().find((component) => component && component.id === activeContext.rootId) || null;
  }

  function getPreferredComponentName(component) {
    return (
      (component && component.name) ||
      (component && component.title) ||
      (component && component.componentType && component.componentType.name) ||
      (component && component.id) ||
      ''
    );
  }

  function getComponentNameCandidates(component) {
    return [
      component && component.name,
      component && component.title,
      component && component.componentType && component.componentType.name,
      component && component.id,
    ].filter(Boolean);
  }

  function getRootVariantDescriptorFromName(rawName) {
    const collapsed = rawName
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '');

    if (!collapsed) return null;

    const layout = collapsed.includes('horizontal')
      ? 'horizontal'
      : collapsed.includes('solid')
        ? 'solid'
        : collapsed.includes('centered') || collapsed.includes('center')
          ? 'centered'
          : collapsed.includes('offset')
            ? 'offset'
            : 'default';
    const platformEnabled = collapsed.includes('noplatform') || collapsed.includes('no_platform') ? false : collapsed.includes('platform');
    const decorVariant = collapsed.includes('nodecor') ? 'nodecor' : collapsed.includes('decor') ? 'decor' : 'default';
    const familyKey = collapsed
      .replace(/noplatform/g, '')
      .replace(/no_platform/g, '')
      .replace(/platform/g, '')
      .replace(/nodecor/g, '')
      .replace(/decor/g, '')
      .replace(/horizontal/g, '')
      .replace(/solid/g, '')
      .replace(/centered/g, '')
      .replace(/center/g, '')
      .replace(/offset/g, '')
      .replace(/bp$/, '');

    if (!familyKey) return null;

    return {
      rawName,
      collapsed,
      familyKey,
      layout,
      platformEnabled,
      decorVariant,
      score: Number(layout !== 'default') + Number(platformEnabled) + Number(decorVariant !== 'default'),
    };
  }

  function getComponentVariantDescriptor(component) {
    const descriptors = getComponentNameCandidates(component)
      .map((candidateName) => getRootVariantDescriptorFromName(candidateName))
      .filter(Boolean)
      .sort((left, right) => right.score - left.score);

    return descriptors[0] || null;
  }

  function getRootVariantDescriptor(component) {
    return getComponentVariantDescriptor(component);
  }

  function resolveComponentVariantForComponent(component) {
    if (!component) return null;

    const targetDescriptor = getComponentVariantDescriptor(component);
    if (!targetDescriptor) return component;

    const componentIsRoot = Boolean(component?.componentType?.isRoot);
    const activeContext = getActiveComponentContext();
    const activeComponent =
      getConfiguratorComponents().find((candidate) => {
        if (!candidate || candidate.id === component.id) return false;
        if (Boolean(candidate?.componentType?.isRoot) !== componentIsRoot) return false;
        if (!activeContext.activeIds.has(candidate.id)) return false;

        const candidateDescriptor = getComponentVariantDescriptor(candidate);
        return candidateDescriptor?.familyKey === targetDescriptor.familyKey;
      }) || null;

    if (!activeComponent) return component;

    const activeDescriptor = getComponentVariantDescriptor(activeComponent);
    if (!activeDescriptor) return component;
    if (activeDescriptor.familyKey !== targetDescriptor.familyKey) return component;

    const desiredPlatformEnabled = activeDescriptor.platformEnabled;
    const desiredDecorVariant =
      activeDescriptor.decorVariant !== 'default' ? activeDescriptor.decorVariant : targetDescriptor.decorVariant;

    return (
      getConfiguratorComponents().find((candidate) => {
        if (Boolean(candidate?.componentType?.isRoot) !== componentIsRoot) return false;

        const candidateDescriptor = getComponentVariantDescriptor(candidate);
        if (!candidateDescriptor) return false;

        return (
          candidateDescriptor.familyKey === targetDescriptor.familyKey &&
          candidateDescriptor.layout === targetDescriptor.layout &&
          candidateDescriptor.platformEnabled === desiredPlatformEnabled &&
          candidateDescriptor.decorVariant === desiredDecorVariant
        );
      }) || component
    );
  }

  function findComponentByName(componentNameNorm) {
    if (!componentNameNorm) return null;
    const components = getConfiguratorComponents();
    return (
      components.find((component) => normalizeEntityName(component && component.name) === componentNameNorm) ||
      components.find((component) => normalizeEntityName(component && component.title) === componentNameNorm) ||
      null
    );
  }

  function findSlotByName(component, slotNameNorm) {
    const slots = component && component.product && component.product.slots;
    if (!Array.isArray(slots) || !slotNameNorm) return null;
    return (
      slots.find((slot) => normalizeEntityName(slot && slot.label) === slotNameNorm) ||
      slots.find((slot) => normalizeEntityName(slot && slot.id) === slotNameNorm) ||
      null
    );
  }

  function findSlotByNameFromSlots(slots, slotNameNorm) {
    if (!Array.isArray(slots) || !slotNameNorm) return null;

    const exactMatch =
      slots.find((slot) => normalizeEntityName(slot && slot.label) === slotNameNorm) ||
      slots.find((slot) => normalizeEntityName(slot && slot.id) === slotNameNorm);
    if (exactMatch) return exactMatch;

    return (
      slots.find((slot) => {
        const labelNorm = normalizeEntityName(slot && slot.label);
        const idNorm = normalizeEntityName(slot && slot.id);

        return (
          (labelNorm && (labelNorm.includes(slotNameNorm) || slotNameNorm.includes(labelNorm))) ||
          (idNorm && (idNorm.includes(slotNameNorm) || slotNameNorm.includes(idNorm)))
        );
      }) || null
    );
  }

  function findMaterialByName(slot, materialNameNorm) {
    const materials = slot && slot.enabledMaterials;
    if (!Array.isArray(materials) || !materialNameNorm) return null;
    return (
      materials.find((material) => normalizeEntityName(material && material.title) === materialNameNorm) ||
      materials.find((material) => normalizeEntityName(material && material.id) === materialNameNorm) ||
      null
    );
  }

  function getComponentIdFromSlot(slot) {
    if (!slot || typeof slot !== 'object') return null;
    if (slot.componentId) return slot.componentId;

    const slotId = slot.id;
    if (typeof slotId !== 'string') return null;

    const separatorIndex = slotId.indexOf('::');
    if (separatorIndex <= 0) return null;
    return slotId.slice(0, separatorIndex);
  }

  function filterSlotsByActiveComponents(slots) {
    const activeContext = getActiveComponentContext();
    if (!activeContext.activeIds.size) return slots;

    const activeSlots = slots.filter((slot) => activeContext.activeIds.has(getComponentIdFromSlot(slot)));
    return activeSlots.length ? activeSlots : slots;
  }

  function pickPreferredSlot(slots) {
    if (!Array.isArray(slots) || slots.length === 0) return undefined;

    const activeContext = getActiveComponentContext();
    if (activeContext.rootId) {
      const rootSlot = slots.find((slot) => getComponentIdFromSlot(slot) === activeContext.rootId);
      if (rootSlot) return rootSlot;
    }

    return slots[0];
  }

  function findMatchingSlot(slots, mwOptionName) {
    const normalizedMwName = normalizeOptionName(mwOptionName);
    if (!normalizedMwName) {
      return undefined;
    }

    const matchingSlots = slots.filter((slot) => {
      const slotLabel = normalizeOptionName(slot.label);
      if (!slotLabel) {
        return false;
      }
      return normalizedMwName.includes(slotLabel) || slotLabel.includes(normalizedMwName);
    });

    if (matchingSlots.length === 0) return undefined;

    const activeMatchingSlots = filterSlotsByActiveComponents(matchingSlots);
    return pickPreferredSlot(activeMatchingSlots.length ? activeMatchingSlots : matchingSlots);
  }

  window.metaboxMwResolver = {
    getActiveComponentContext,
    getConfiguratorComponents,
    getRootComponents,
    getActiveRootComponent,
    getPreferredComponentName,
    getRootVariantDescriptor,
    resolveComponentVariantForComponent,
    findComponentByName,
    findSlotByName,
    findSlotByNameFromSlots,
    findMaterialByName,
    getComponentIdFromSlot,
    filterSlotsByActiveComponents,
    pickPreferredSlot,
    findMatchingSlot,
  };
})();
